// =============================================================================
// VandieZ admin-web -- AdminLayout (sidebar-first shell)
// =============================================================================
// Single left sidebar holds every piece of chrome:
//
//   Top         brand (collapsible toggle) + search input
//   Middle      navigation links with optional count badges + active
//               indicator (blue left bar + light blue tint)
//   Bottom      Settings + Logout, Light/Dark theme pill, user profile
//               card (avatar, name, email)
//
// Mobile: sidebar overlays from the left.
// Collapsed: w-16, labels hidden, hover-tooltips on icons.
// Theme: light/dark toggle on the bottom pill flips the `.dark` class
//        via the ThemeProvider; CSS variables already support both.
// Search: visual placeholder for now (Phase E global-search lands later).
//
// `App.tsx` mounts <AdminLayout/> as a layout route; pages render bare
// content inside the <Outlet/>.
// =============================================================================

import {
  Activity,
  ArrowLeftRight,
  BarChart3,
  BellRing,
  ChevronRight,
  ChevronsLeft,
  ClipboardCheck,
  ShieldAlert,
  ClipboardList,
  Gift,
  Headphones,
  Landmark,
  LifeBuoy,
  LogOut,
  Map,
  MapPin,
  Route,
  Menu,
  MessageSquare,
  Moon,
  Package,
  IndianRupee,
  Ban,
  Boxes,
  Percent,
  PhoneCall,
  Scale,
  Search,
  Send,
  Settings,
  Shield,
  ShieldCheck,
  Siren,
  Sun,
  Truck,
  Users,
  Wallet,
  X,
} from "lucide-react";
import type React from "react";
import { useEffect, useState } from "react";
import { Link, NavLink, Outlet, useLocation } from "react-router-dom";

import { GlobalSearch } from "@/components/GlobalSearch";
import { useAuth } from "@/lib/auth";
import { useNavCounts } from "@/lib/nav-counts";
import { ToastProvider } from "@/lib/toast";
import {
  SidebarCollapseProvider,
  ThemeProvider,
  useCollapsed,
  useTheme,
} from "@/lib/theme";
import { cn } from "@/lib/utils";

// ---------------------------------------------------------------------------
// Navigation definition
// ---------------------------------------------------------------------------
type IconComponent = typeof BarChart3;

type CountKey = "kyc" | "vehicles" | "disputes" | "tickets" | "inbox" | "atRisk";

interface NavItem {
  to: string;
  label: string;
  icon: IconComponent;
  countKey?: CountKey;
  // Extra URL prefixes that should ALSO light this item up. The
  // default NavLink behaviour highlights when the current path
  // STARTS with `to`, which fails for detail routes that share a
  // /trips/* parent with a list at /trips/live (the strings don't
  // share a prefix). List those siblings here.
  activePrefixes?: string[];
}

const NAV_PRIMARY: NavItem[] = [
  { to: "/", label: "Dashboard", icon: BarChart3 },
  // /trips/live matches the list; /trips/:tripId is the detail page and
  // should keep Live trips highlighted.
  {
    to: "/trips/live",
    label: "Live trips",
    icon: Truck,
    activePrefixes: ["/trips/"],
  },
  // /kyc/queue is the list; /kyc/users/:userId is the per-user review.
  //
  // The prefixes are listed explicitly rather than as a bare "/kyc/",
  // which would also swallow /kyc/review-flags and leave two sidebar
  // items lit at once.
  {
    to: "/kyc/queue",
    label: "KYC queue",
    icon: ClipboardCheck,
    countKey: "kyc",
    activePrefixes: ["/kyc/queue", "/kyc/users/"],
  },
  // D-20. A different question from the queue above: not "approve this
  // account" but "this approved account changed its identity". It stays
  // trading while it sits here, so it is a backlog, not an outage.
  {
    to: "/kyc/review-flags",
    label: "Identity changes",
    icon: ShieldAlert,
    activePrefixes: ["/kyc/review-flags"],
  },
  // /vehicles/queue is the list; /vehicles/:vehicleId is the detail.
  {
    to: "/vehicles/queue",
    label: "Vehicle queue",
    icon: Truck,
    countKey: "vehicles",
    activePrefixes: ["/vehicles/"],
  },
  // Sits under Vehicle queue: a row parked here holds its vehicle's only
  // live transfer slot, so the truck is stuck until someone works this list.
  {
    to: "/vehicle-transfers/pending",
    label: "Transfer approvals",
    icon: ArrowLeftRight,
  },
  { to: "/ops", label: "Ops", icon: BarChart3 },
  { to: "/payouts", label: "Payouts", icon: Send },
  // Sits directly under Payouts: since DR-24 a payout is blocked until the
  // destination account clears this queue, so the two are worked together.
  { to: "/bank-accounts/pending", label: "Bank verification", icon: Landmark },
  { to: "/promotions", label: "Promotions", icon: Gift },
  { to: "/wallet/audit", label: "Wallet audit", icon: Wallet },
  { to: "/commission/rules", label: "Commission", icon: Percent },
  { to: "/cancellation/policies", label: "Cancellation", icon: Ban },
  { to: "/pricing", label: "Pricing", icon: IndianRupee },
  { to: "/masterdata/locations", label: "Locations", icon: MapPin },
  { to: "/masterdata/zones", label: "Zones", icon: Map },
  { to: "/masterdata/routes", label: "Routes & tolls", icon: Route },
  { to: "/masterdata/business", label: "Business masters", icon: Boxes },
  { to: "/disputes", label: "Disputes", icon: Scale, countKey: "disputes" },
  { to: "/tickets", label: "Tickets", icon: LifeBuoy, countKey: "tickets" },
  { to: "/mediation", label: "Mediation", icon: Headphones },
  { to: "/bid-calls", label: "Bid calls", icon: PhoneCall },
  { to: "/loads", label: "Loads", icon: Package, activePrefixes: ["/loads/"] },
  { to: "/notifications", label: "Push", icon: BellRing },
  { to: "/inbox", label: "Inbox", icon: BellRing, countKey: "inbox" },
  { to: "/settings/sms", label: "SMS", icon: MessageSquare },
  { to: "/audit-log", label: "Audit log", icon: ClipboardList },
  { to: "/fraud", label: "Fraud signals", icon: Shield },
  { to: "/at-risk", label: "Trips at risk", icon: Siren, countKey: "atRisk" },
  { to: "/system-health", label: "System health", icon: Activity },
  { to: "/users", label: "Users", icon: Users },
  { to: "/roles", label: "Roles", icon: ShieldCheck },
];

// ---------------------------------------------------------------------------
// Sidebar internals
// ---------------------------------------------------------------------------
function Sidebar({
  mobileOpen,
  onCloseMobile,
}: {
  mobileOpen: boolean;
  onCloseMobile: () => void;
}): React.JSX.Element {
  const { user, logout, accessToken } = useAuth();
  const { collapsed, toggle: toggleCollapsed } = useCollapsed();
  const { theme, setTheme } = useTheme();
  const counts = useNavCounts(accessToken);

  return (
    <aside
      className={cn(
        "border-r bg-background flex flex-col shrink-0",
        // Mobile (<md): off-canvas drawer, slides in over the content.
        "fixed inset-y-0 left-0 z-40 transition-transform duration-200",
        mobileOpen ? "translate-x-0" : "-translate-x-full",
        // md and up: in-flow sticky sidebar.
        "md:sticky md:top-0 md:translate-x-0 md:h-screen md:transition-[width]",
        collapsed ? "md:w-16" : "md:w-64",
        // Mobile width: always full sidebar (collapse doesn't apply).
        "w-64",
        "h-screen overflow-y-auto",
      )}
      aria-label="Admin navigation"
      onClick={(e) => { e.stopPropagation(); }}
    >
      {/* Brand + collapse toggle */}
      <div
        className={cn(
          "flex items-center px-3 py-4 border-b",
          collapsed ? "md:justify-center" : "justify-between",
        )}
      >
        {!collapsed && (
          <Link to="/" className="flex items-center gap-2">
            <Truck className="h-6 w-6 text-primary" />
            <span className="text-lg font-semibold tracking-tight">
              VandieZ
            </span>
          </Link>
        )}
        {collapsed && (
          <Link to="/" className="hidden md:block">
            <Truck className="h-6 w-6 text-primary" />
          </Link>
        )}
        {/* Desktop collapse toggle (chevron) */}
        {!collapsed && (
          <button
            type="button"
            aria-label="Collapse sidebar"
            className="hidden md:block p-1 rounded hover:bg-muted text-muted-foreground"
            onClick={toggleCollapsed}
          >
            <ChevronsLeft className="h-4 w-4" />
          </button>
        )}
        {/* Mobile close button */}
        <button
          type="button"
          aria-label="Close menu"
          className="md:hidden p-1 rounded hover:bg-muted text-muted-foreground"
          onClick={onCloseMobile}
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      {/* Search */}
      {!collapsed ? (
        <div className="px-3 pt-3">
          <GlobalSearch />
        </div>
      ) : (
        <div className="px-3 pt-3 flex justify-center">
          <button
            type="button"
            aria-label="Search"
            className="p-1.5 rounded hover:bg-muted text-muted-foreground"
            onClick={toggleCollapsed}
            title="Expand sidebar to search"
          >
            <Search className="h-4 w-4" />
          </button>
        </div>
      )}

      {/* Primary nav */}
      <nav className="flex-1 overflow-y-auto px-2 py-3">
        <ul className="space-y-0.5">
          {NAV_PRIMARY.map((item) => (
            <NavRow
              key={item.to}
              item={item}
              count={item.countKey !== undefined ? counts[item.countKey] : null}
              collapsed={collapsed}
            />
          ))}
        </ul>
      </nav>

      {/* Bottom block: Settings + Logout + theme toggle + user card */}
      <div className="border-t px-2 py-3 space-y-1">
        <NavRow
          item={{ to: "/settings/sms", label: "Settings", icon: Settings }}
          count={null}
          collapsed={collapsed}
        />
        <button
          type="button"
          onClick={logout}
          className={cn(
            "w-full flex items-center rounded-md text-sm",
            "hover:bg-muted text-foreground",
            collapsed ? "justify-center p-2" : "gap-2 px-2 py-1.5",
          )}
          aria-label="Logout"
          title={collapsed ? "Logout" : undefined}
        >
          <LogOut className="h-4 w-4 shrink-0" />
          {!collapsed && <span>Logout</span>}
        </button>

        {/* Theme pill */}
        {!collapsed ? (
          <div className="mt-2 rounded-full border bg-muted/40 p-0.5 flex text-xs">
            <button
              type="button"
              onClick={() => { setTheme("light"); }}
              className={cn(
                "flex-1 flex items-center justify-center gap-1 rounded-full py-1 transition-colors",
                theme === "light"
                  ? "bg-background shadow-sm font-medium"
                  : "text-muted-foreground",
              )}
              aria-pressed={theme === "light"}
              aria-label="Light theme"
            >
              <Sun className="h-3.5 w-3.5" /> Light
            </button>
            <button
              type="button"
              onClick={() => { setTheme("dark"); }}
              className={cn(
                "flex-1 flex items-center justify-center gap-1 rounded-full py-1 transition-colors",
                theme === "dark"
                  ? "bg-background shadow-sm font-medium"
                  : "text-muted-foreground",
              )}
              aria-pressed={theme === "dark"}
              aria-label="Dark theme"
            >
              <Moon className="h-3.5 w-3.5" /> Dark
            </button>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => { setTheme(theme === "dark" ? "light" : "dark"); }}
            className="w-full flex items-center justify-center p-2 rounded-md hover:bg-muted text-muted-foreground"
            aria-label="Toggle theme"
            title={`Switch to ${theme === "dark" ? "light" : "dark"} theme`}
          >
            {theme === "dark" ? (
              <Sun className="h-4 w-4" />
            ) : (
              <Moon className="h-4 w-4" />
            )}
          </button>
        )}

        {/* User profile card */}
        <div
          className={cn(
            "mt-2 border-t pt-3 flex items-center",
            collapsed ? "justify-center" : "gap-2 px-1",
          )}
        >
          <Avatar name={user?.full_name ?? "?"} />
          {!collapsed && (
            <div className="min-w-0 flex-1">
              <div className="text-sm font-medium truncate">
                {user?.full_name ?? "—"}
              </div>
              <div className="text-xs text-muted-foreground truncate font-mono">
                {user?.phone ?? ""}
              </div>
            </div>
          )}
        </div>
      </div>
    </aside>
  );
}

function NavRow({
  item,
  count,
  collapsed,
}: {
  item: NavItem;
  count: number | null;
  collapsed: boolean;
}): React.JSX.Element {
  const location = useLocation();
  // Manual active matcher so detail routes light up their parent nav.
  // Dashboard (to="/") uses exact match; everything else matches when
  // the current path starts with `to` OR with any of the extra
  // activePrefixes patterns the NavItem declares.
  const path = location.pathname;
  const isActive =
    item.to === "/"
      ? path === "/"
      : path === item.to ||
        path.startsWith(`${item.to}/`) ||
        (item.activePrefixes?.some((p) => path.startsWith(p)) ?? false);

  return (
    <li>
      <NavLink
        to={item.to}
        className={cn(
          "group relative flex items-center rounded-md text-sm",
          "hover:bg-muted",
          // Larger touch target — matches the ~40 px row height in the
          // reference design. Both modes use the same height so the
          // active edge-bar lines up cleanly.
          collapsed ? "justify-center h-10" : "gap-2.5 px-2.5 h-10",
          isActive
            ? "bg-primary-subtle text-primary-subtle-foreground font-medium"
            : "text-foreground",
        )}
        title={collapsed ? item.label : undefined}
      >
        {/* Primary (brand-blue) active-edge bar at the sidebar's left edge.  Stays
            visible on both expanded + collapsed columns so the
            operator can identify the active page even on the rail. */}
        {isActive && (
          <span
            className="absolute left-0 top-1.5 bottom-1.5 w-0.5 rounded-r bg-primary"
            aria-hidden
          />
        )}
        <item.icon className="h-4 w-4 shrink-0" />
        {!collapsed && (
          <>
            <span className="flex-1 truncate">{item.label}</span>
            {count !== null && count > 0 && (
              <span
                className={cn(
                  "ml-auto rounded-full px-1.5 py-0.5 text-xs font-medium tabular-nums",
                  isActive
                    ? "bg-primary text-primary-foreground"
                    : "bg-muted text-foreground",
                )}
              >
                {count}
              </span>
            )}
          </>
        )}
        {collapsed && count !== null && count > 0 && (
          <span
            className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 rounded-full bg-danger text-danger-foreground text-[10px] font-medium flex items-center justify-center px-1"
            aria-label={`${count} pending`}
          >
            {count > 9 ? "9+" : count}
          </span>
        )}
      </NavLink>
    </li>
  );
}

function Avatar({ name }: { name: string }): React.JSX.Element {
  // Two-letter monogram from the user's name, on a fixed brand-tinted chip.
  const initials = name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase() ?? "")
    .join("") || "?";
  return (
    <div
      className="h-8 w-8 rounded-full bg-primary-subtle text-primary-subtle-foreground flex items-center justify-center text-xs font-semibold shrink-0"
      aria-hidden
    >
      {initials}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Layout root + page header helper
// ---------------------------------------------------------------------------
export function AdminLayout(): React.JSX.Element {
  return (
    <ThemeProvider>
      <SidebarCollapseProvider>
        <ToastProvider>
          <AdminLayoutShell />
        </ToastProvider>
      </SidebarCollapseProvider>
    </ThemeProvider>
  );
}

function AdminLayoutShell(): React.JSX.Element {
  const [mobileOpen, setMobileOpen] = useState<boolean>(false);
  const location = useLocation();

  // Auto-close the mobile drawer on route change so the user doesn't
  // have to dismiss it manually after every navigation.
  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  return (
    <div className="flex min-h-screen bg-muted/30">
      {/* Mobile backdrop */}
      {mobileOpen && (
        <button
          type="button"
          aria-label="Close menu"
          className="fixed inset-0 z-30 bg-black/40 md:hidden"
          onClick={() => { setMobileOpen(false); }}
        />
      )}

      <Sidebar
        mobileOpen={mobileOpen}
        onCloseMobile={() => { setMobileOpen(false); }}
      />

      <div className="flex-1 min-w-0 flex flex-col">
        {/* Mobile top bar with hamburger (hidden on md+) */}
        <header className="sticky top-0 z-20 bg-background border-b px-3 py-2 flex items-center gap-2 md:hidden">
          <button
            type="button"
            aria-label="Open navigation"
            className="p-1.5 rounded hover:bg-muted"
            onClick={() => { setMobileOpen(true); }}
          >
            <Menu className="h-5 w-5" />
          </button>
          <Link to="/" className="flex items-center gap-2">
            <Truck className="h-5 w-5 text-primary" />
            <span className="font-semibold">VandieZ</span>
          </Link>
        </header>

        <main className="flex-1 px-4 py-6 md:px-8">
          <div className="mx-auto max-w-6xl">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
}

/**
 * Lightweight page header used inside AdminLayout's <Outlet>.
 * Pages render <PageHeader title="..." description="..." actions={...} />
 * at the top of their content.
 */
export function PageHeader({
  title,
  description,
  actions,
}: {
  title: string;
  description?: React.ReactNode;
  actions?: React.ReactNode;
}): React.JSX.Element {
  return (
    <div className="mb-6 flex flex-wrap items-start justify-between gap-3">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">{title}</h1>
        {description !== undefined && (
          <p className="text-sm text-muted-foreground mt-1">{description}</p>
        )}
      </div>
      {actions !== undefined && (
        <div className="flex items-center gap-2">{actions}</div>
      )}
    </div>
  );
}

// Expose an "expand on click while collapsed" affordance for hidden actions
// (kept as the same toggle on the sidebar's chevron button when expanded).
export function ExpandSidebarHandle(): React.JSX.Element {
  const { toggle } = useCollapsed();
  return (
    <button
      type="button"
      onClick={toggle}
      aria-label="Expand sidebar"
      className="p-1 rounded hover:bg-muted text-muted-foreground"
    >
      <ChevronRight className="h-4 w-4" />
    </button>
  );
}
